/**
 * Trade Confirmation Modal
 *
 * Displays a modal confirmation when a fill is emitted by the WebSocket.
 * Shows order summary (side, symbol, quantity, price, total), fee breakdown,
 * and action buttons.
 *
 * Props:
 *   - isOpen: boolean — whether the modal is visible
 *   - onClose: () => void — called when user closes the modal
 *   - fillData: FillEvent payload shaped as { clOrdId, symbol, side, quantity, averagePrice, filledAt, fillRef }
 */

import { useMemo } from 'react';
import type { WsEvent } from '../api/wsClient';

interface FillPayload {
  clOrdId: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  averagePrice: string | number;
  filledAt: string;
  fillRef: string;
}

interface TradeConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  fillData: FillPayload | null;
}

/**
 * Fee rates for Nigerian equities trading.
 * Used to compute the fee breakdown on fills.
 */
const EQUITY_FEE_RATES = {
  brokeragePct: 0.75,
  vatBrokeragePct: 7.5,
  ngxPct: 0.003,
  secPct: 0.0003,
  cscsPct: 0.006,
  vatSecPct: 7.5,
  stampDutyPct: 0.075, // BUY only
  terminalPct: 0.001,
};

interface FeeBreakdown {
  principal: bigint;
  brokerage: bigint;
  vatOnBrokerage: bigint;
  ngxFee: bigint;
  vatOnNgx: bigint;
  secFee: bigint;
  vatOnSec: bigint;
  cscsFee: bigint;
  vatOnCscs: bigint;
  stampDuty: bigint;
  terminalFee: bigint;
  totalFees: bigint;
  totalCash: bigint;
}

/**
 * Compute fee breakdown for an equity trade.
 * All inputs and outputs in kobo (1 kobo = 1/100 NGN).
 */
function computeEquityFees(
  side: 'BUY' | 'SELL',
  quantityShares: number,
  fillPriceNaira: number
): FeeBreakdown {
  // Convert price to kobo (multiply by 100)
  const priceKobo = BigInt(Math.round(fillPriceNaira * 100));
  const quantity = BigInt(quantityShares);

  // Principal = quantity × price
  const principal = quantity * priceKobo;

  // Brokerage: 0.75% of principal
  const brokerageKobo = (principal * BigInt(75)) / BigInt(10000);
  const vatOnBrokerage = (brokerageKobo * BigInt(750)) / BigInt(100000); // 7.5% of brokerage

  // NGX fee: 0.003% of principal
  const ngxFeeKobo = (principal * BigInt(3)) / BigInt(100000);
  const vatOnNgx = (ngxFeeKobo * BigInt(750)) / BigInt(100000); // 7.5% of NGX fee

  // SEC fee: 0.0003% of principal
  const secFeeKobo = (principal * BigInt(3)) / BigInt(1000000);
  const vatOnSec = (secFeeKobo * BigInt(750)) / BigInt(100000); // 7.5% of SEC fee

  // CSCS fee: 0.006% of principal
  const cscsFeeKobo = (principal * BigInt(6)) / BigInt(100000);
  const vatOnCscs = (cscsFeeKobo * BigInt(750)) / BigInt(100000); // 7.5% of CSCS fee

  // Stamp duty: 0.075% of principal (BUY only)
  const stampDutyKobo = side === 'BUY' ? (principal * BigInt(75)) / BigInt(100000) : 0n;

  // Terminal fee (Nigastock): 0.001% of principal
  const terminalFeeKobo = (principal * BigInt(1)) / BigInt(100000);

  // Total fees
  const totalFees = brokerageKobo + vatOnBrokerage + ngxFeeKobo + vatOnNgx
    + secFeeKobo + vatOnSec + cscsFeeKobo + vatOnCscs + stampDutyKobo + terminalFeeKobo;

  // Total cash due/received
  const totalCash = side === 'BUY' ? principal + totalFees : principal - totalFees;

  return {
    principal,
    brokerage: brokerageKobo,
    vatOnBrokerage,
    ngxFee: ngxFeeKobo,
    vatOnNgx,
    secFee: secFeeKobo,
    vatOnSec,
    cscsFee: cscsFeeKobo,
    vatOnCscs,
    stampDuty: stampDutyKobo,
    terminalFee: terminalFeeKobo,
    totalFees,
    totalCash,
  };
}

/**
 * Format kobo to naira string (e.g. 150000 kobo → ₦1,500.00)
 */
function koboToNaira(k: bigint): string {
  const naira = Number(k) / 100;
  return '₦' + naira.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * FeeRow — reusable component for displaying a fee line.
 */
function FeeRow({ k, v, bold, className }: { k: string; v: string; bold?: boolean; className?: string }) {
  return (
    <div className={'fee-row ' + (className ?? '')} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontWeight: bold ? 600 : 400 }}>
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}

export function TradeConfirmModal({ isOpen, onClose, fillData }: TradeConfirmModalProps) {
  const fees = useMemo<FeeBreakdown | undefined>(() => {
    if (!fillData) return undefined;
    try {
      const price = typeof fillData.averagePrice === 'string'
        ? Number(fillData.averagePrice)
        : fillData.averagePrice;
      return computeEquityFees(fillData.side, fillData.quantity, price);
    } catch {
      return undefined;
    }
  }, [fillData]);

  if (!isOpen || !fillData) {
    return null;
  }

  const sideColor = fillData.side === 'BUY' ? 'var(--green)' : '#ff8c00'; // orange for SELL
  const sideLabel = fillData.side === 'BUY' ? 'BUY' : 'SELL';
  const fillPrice = typeof fillData.averagePrice === 'string'
    ? Number(fillData.averagePrice)
    : fillData.averagePrice;

  return (
    <div className="trade-confirm-overlay" onClick={onClose}>
      <div className="trade-confirm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="trade-confirm-header">
          <div className="checkmark-icon">✓</div>
          <h2>Trade Confirmed</h2>
        </div>

        {/* Body — Order Summary */}
        <div className="trade-confirm-body">
          <div className="order-summary">
            <div className="summary-row">
              <span className="label">Side</span>
              <span className="value" style={{ color: sideColor, fontWeight: 700 }}>{sideLabel}</span>
            </div>
            <div className="summary-row">
              <span className="label">Symbol</span>
              <span className="value mono">{fillData.symbol}</span>
            </div>
            <div className="summary-row">
              <span className="label">Quantity</span>
              <span className="value mono">{fillData.quantity.toLocaleString('en-NG')}</span>
            </div>
            <div className="summary-row">
              <span className="label">Fill Price</span>
              <span className="value mono">₦{fillPrice.toFixed(2)}</span>
            </div>
            {fees && (
              <div className="summary-row highlight">
                <span className="label">Total Consideration</span>
                <span className="value mono">{koboToNaira(fees.principal)}</span>
              </div>
            )}
          </div>

          {/* Fee Breakdown */}
          {fees && (
            <div className="fee-breakdown">
              <div className="fee-section-title">Fee Breakdown</div>
              <FeeRow k="Brokerage (0.75%)" v={koboToNaira(fees.brokerage)} />
              <FeeRow k="VAT on Brokerage (7.5%)" v={koboToNaira(fees.vatOnBrokerage)} />
              <FeeRow k="NGX Fee (0.003%)" v={koboToNaira(fees.ngxFee)} />
              <FeeRow k="VAT on NGX (7.5%)" v={koboToNaira(fees.vatOnNgx)} />
              <FeeRow k="SEC Fee (0.0003%)" v={koboToNaira(fees.secFee)} />
              <FeeRow k="VAT on SEC (7.5%)" v={koboToNaira(fees.vatOnSec)} />
              <FeeRow k="CSCS Fee (0.006%)" v={koboToNaira(fees.cscsFee)} />
              <FeeRow k="VAT on CSCS (7.5%)" v={koboToNaira(fees.vatOnCscs)} />
              {fillData.side === 'BUY' && (
                <FeeRow k="Stamp Duty (0.075%)" v={koboToNaira(fees.stampDuty)} />
              )}
              <FeeRow k="Terminal Fee (0.001%)" v={koboToNaira(fees.terminalFee)} />
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, marginBottom: 8 }} />
              <FeeRow
                k="Total Fees"
                v={koboToNaira(fees.totalFees)}
                bold
                className={fillData.side === 'SELL' ? 'up' : 'down'}
              />
              <FeeRow
                k={fillData.side === 'BUY' ? 'Total Cash Due' : 'Total Cash Received'}
                v={koboToNaira(fees.totalCash)}
                bold
              />
            </div>
          )}

          {/* Metadata */}
          <div className="meta-info">
            <div className="meta-row">
              <span className="label">Fill Ref</span>
              <span className="value mono small">{fillData.fillRef}</span>
            </div>
            <div className="meta-row">
              <span className="label">Filled At</span>
              <span className="value small">
                {new Date(fillData.filledAt).toLocaleTimeString('en-NG', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="trade-confirm-footer">
          <button className="close-btn" onClick={onClose}>Close</button>
          <a href="#/Orders" className="view-link">View in Orders</a>
        </div>
      </div>
    </div>
  );
}
